import { useEffect, useMemo, useState } from 'react'
import { alpha, useTheme } from '@mui/material/styles'
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, MenuItem, Select,
  Stack, TextField, Tooltip, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined'
import QrCode2OutlinedIcon from '@mui/icons-material/QrCode2Outlined'
import QuestionAnswerOutlinedIcon from '@mui/icons-material/QuestionAnswerOutlined'
import { motion } from 'framer-motion'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@cmms/api-client'
import type { Location as Loc, LocationTreeNode, Site } from '@cmms/types'
import { primary } from '../../theme/palette'

import { PageShell } from '../../layouts/PageShell'
import { api } from '../../lib/api'
import { toast } from '../../store/snackbarStore'
import { useAuthStore } from '../../store/authStore'
import { QrCodeDialog, qrPayload } from '../../components/QrCodeDialog'
import { renderQrCard, loadLogo, safeFilename } from '../../lib/qrCard'
import { downloadQrPdf } from '../../lib/qrPdf'
import { TagsField } from '../../components/TagsField'
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined'
import { LocationTree, type TreeContextAction } from './components/LocationTree'
import { LocationDetail } from './components/LocationDetail'
import { CategoriesDialog } from './components/CategoriesDialog'
import { CreateDialog } from './components/LocationDetail'
import { ImportDialog } from './components/ImportDialog'
import { MapPicker } from '../../components/MapPicker'
import { MapView } from './components/MapView'
import { AllSitesSidebar } from './components/AllSitesSidebar'
import { FilterStrip } from './components/FilterStrip'
import {
  buildLegend, colorForValue, getDimensionValue,
  type ColorBy,
} from './lib/siteColors'
import { useConfirm } from '../../components/ConfirmDialog'
import { usePermissions } from '../../hooks/usePermissions'
import { useBulkSelection } from '../../hooks/useBulkSelection'
import { useOrgSettings } from '../../hooks/useOrgSettings'
import { useSiteFieldLabels } from '../../hooks/useSiteFieldLabels'
import { formatSiteName } from '../../lib/siteDisplayName'

type Selection = { type: 'site' } | { type: 'location'; id: string }

export function LocationsPage() {
  const navigate = useNavigate()
  const { locationId: urlLocationId } = useParams<{ locationId?: string }>()
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'

  const confirmDlg = useConfirm()
  const qc = useQueryClient()
  const { can } = usePermissions()

  const [selection, setSelection] = useState<Selection | null>(null)
  const [search, setSearch] = useState('')
  const [catDialogOpen, setCatDialogOpen] = useState(false)
  const [createRootOpen, setCreateRootOpen] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<LocationTreeNode | null>(null)
  const [createTarget2, setCreateTarget2] = useState<LocationTreeNode | null>(null)
  const [resolvedFromUrl, setResolvedFromUrl] = useState(false)
  // 'all-sites' is the default — sidebar lists every site, the right
  // pane shows a centralised map. Clicking a site (pin or row) flips
  // into 'site' mode and reveals the existing tree + detail layout.
  // A URL with a locationId forces 'site' mode pre-resolution.
  const [viewMode, setViewMode] = useState<'all-sites' | 'site'>(
    urlLocationId ? 'site' : 'all-sites'
  )
  // Colour-coding dimension for the map pins + sidebar dots.
  const [colorBy, setColorBy] = useState<ColorBy>('none')
  // Multi-select filters — applied alongside the text search to narrow
  // the set shown on the map + in the sidebar.
  const [entityFilter, setEntityFilter] = useState<string[]>([])
  const [stateFilter, setStateFilter] = useState<string[]>([])
  const [categoryFilter, setCategoryFilter] = useState<string[]>([])
  // Legend overlay highlight — clicking a legend entry dims pins that
  // don't match. null = no highlight.
  const [highlightedLegendValue, setHighlightedLegendValue] = useState<string | null>(null)
  // Clear the legend highlight whenever the dimension itself changes
  // — the old highlighted value is no longer meaningful.
  useEffect(() => { setHighlightedLegendValue(null) }, [colorBy])

  // Both heavy queries pin a generous staleTime so navigating back to
  // the page (or switching between locations on the tree) reuses the
  // cached data instead of replaying the spinner — that's the
  // "sometimes the screen keeps spinning" the customer reported. The
  // user-triggered actions (create / delete / rename) all invalidate
  // explicitly, so freshness on data the user actually edits stays
  // correct.
  const sitesQ = useQuery({ queryKey: ['sites'], queryFn: () => api.sites.list(), staleTime: 5 * 60_000 })
  const sites: Site[] = Array.isArray(sitesQ.data) ? sitesQ.data : []

  // Filtered site list for the all-sites view. Combines:
  //   - Free-text search (name OR address — Autocomplete is scoped to
  //     these two fields so users typing "Jaipur" don't pull every
  //     site in Rajasthan via the state column).
  //   - Multi-select chips for entity / state / category (OR within
  //     each dimension, AND across dimensions).
  const filteredSites = useMemo(() => {
    const q = search.trim().toLowerCase()
    const entitySet   = new Set(entityFilter)
    const stateSet    = new Set(stateFilter)
    const categorySet = new Set(categoryFilter)
    return sites.filter((s) => {
      if (q) {
        const hay = [s.name, s.address].filter(Boolean).join(' ').toLowerCase()
        if (!hay.includes(q)) return false
      }
      if (entitySet.size > 0   && !(s.entity   && entitySet.has(s.entity)))     return false
      if (stateSet.size > 0    && !(s.state    && stateSet.has(s.state)))       return false
      if (categorySet.size > 0 && !(s.category && categorySet.has(s.category))) return false
      return true
    })
  }, [sites, search, entityFilter, stateFilter, categoryFilter])

  // Legend + per-site colour, computed off the filtered set so counts
  // and colours reflect what the user is actually looking at.
  const legend = useMemo(
    () => buildLegend(filteredSites, colorBy, isDark),
    [filteredSites, colorBy, isDark]
  )
  const getSiteColor = useMemo(() => {
    if (colorBy === 'none') return undefined
    return (s: Site): string | undefined =>
      colorForValue(getDimensionValue(s, colorBy), isDark)
  }, [colorBy, isDark])
  const getSiteDimensionValue = useMemo(() => {
    if (colorBy === 'none') return undefined
    return (s: Site): string | null => getDimensionValue(s, colorBy)
  }, [colorBy])

  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null)
  // In all-sites mode the right pane is the map, so we deliberately
  // skip auto-picking the first site — that used to be the fallback
  // when the user hadn't chosen one. In site mode, we still default
  // to the first site if nothing is explicitly selected (rare; user
  // would have had to clear selection somehow).
  const activeSiteId = viewMode === 'site'
    ? (selectedSiteId ?? sites[0]?.id ?? null)
    : null
  const activeSite = activeSiteId ? sites.find((s) => s.id === activeSiteId) : null

  // If URL has a locationId, we need to figure out which site it belongs to.
  // First try searching all sites' trees. The search endpoint returns site_id.
  const urlLocQ = useQuery({
    queryKey: ['location-lookup', urlLocationId],
    queryFn: () => {
      // Use the list endpoint with each site to find the location.
      // Simpler: use search endpoint if name is known, but we only have ID.
      // Fallback: try fetching from each site.
      return Promise.all(
        sites.map((s) => api.locations.get(s.id, urlLocationId!).catch(() => null))
      ).then((results) => results.find((r) => r != null) ?? null)
    },
    enabled: Boolean(urlLocationId) && sites.length > 0 && !resolvedFromUrl,
  })

  // When the URL location is resolved, switch into site mode for its
  // site and select it. URL deeplinks always land in single-site view.
  useEffect(() => {
    if (urlLocQ.data && !resolvedFromUrl) {
      const loc = urlLocQ.data as Loc
      setSelectedSiteId(loc.site_id)
      setViewMode('site')
      setSelection({ type: 'location', id: loc.id })
      setResolvedFromUrl(true)
    }
  }, [urlLocQ.data, resolvedFromUrl])

  const treeQ = useQuery({
    queryKey: ['location-tree', activeSiteId],
    queryFn: () => api.locations.tree(activeSiteId!),
    enabled: Boolean(activeSiteId),
    // Reuse the tree across re-mounts and tab switches — large
    // location hierarchies were re-fetching on every navigation
    // back to the page, which felt like an indefinite spinner.
    staleTime: 60_000,
  })
  const tree: LocationTreeNode[] = Array.isArray(treeQ.data) ? treeQ.data : []

  // Auto-select site root only when we're already in site mode —
  // in all-sites mode the map is the right-pane, not a site detail.
  if (viewMode === 'site' && !selection && !urlLocationId && activeSite && !sitesQ.isLoading) {
    setSelection({ type: 'site' })
  }

  // Pin/row click → enter single-site view for that site.
  function handlePickSite(siteId: string) {
    setSelectedSiteId(siteId)
    setViewMode('site')
    setSelection({ type: 'site' })
    navigate('/locations', { replace: true })
  }

  // Back button → return to the centralised all-sites map.
  function handleBackToAllSites() {
    setViewMode('all-sites')
    setSelection(null)
    navigate('/locations', { replace: true })
  }

  const selectedLocationId = selection?.type === 'location' ? selection.id : null

  const selectedLocation = useMemo(() => {
    if (!selectedLocationId) return null
    function find(nodes: LocationTreeNode[]): Loc | null {
      for (const n of nodes ?? []) {
        if (n.id === selectedLocationId) return n as Loc
        const r = find(n.children ?? [])
        if (r) return r
      }
      return null
    }
    return find(tree)
  }, [tree, selectedLocationId])

  // Update URL when selection changes (don't trigger on initial URL resolution)
  function handleSelectLocation(id: string) {
    setSelection({ type: 'location', id })
    navigate(`/locations/${id}`, { replace: true })
  }

  function handleSelectSite() {
    setSelection({ type: 'site' })
    navigate('/locations', { replace: true })
  }

  const createTarget = useMemo(() => {
    if (selection?.type === 'location' && selectedLocation) {
      return { name: selectedLocation.name, parentId: selectedLocation.id, parentType: selectedLocation.type }
    }
    if (activeSite) {
      return { name: activeSite.name, parentId: undefined, parentType: undefined }
    }
    return null
  }, [selection, selectedLocation, activeSite])

  // Delete mutation for right-click delete
  const deleteMutation = useMutation({
    mutationFn: (locId: string) => api.locations.remove(activeSiteId!, locId),
    onSuccess: () => { toast.success('Location deleted'); qc.invalidateQueries({ queryKey: ['location-tree', activeSiteId] }) },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to delete'),
  })

  const bulk = useBulkSelection()
  // Reset the selection when the user switches sites — ids only mean
  // something inside the active site's tree.
  useEffect(() => { bulk.clear() }, [activeSiteId])

  // Bulk QR-PDF export. Runs entirely client-side: for each selected
  // location we render a QR-sticker canvas via the org's saved template
  // (same visuals as the single-print dialog), then lay every card into
  // a 5-column A4 grid PDF. Multi-page pagination is automatic — a
  // batch of 100+ QRs just spills onto more pages instead of shipping
  // a folder of loose PNGs.
  const orgSlug = useAuthStore((s) => s.user?.org_slug ?? null)
  const { prefs: orgPrefs } = useOrgSettings()
  const bulkQrZipM = useMutation({
    mutationFn: async (ids: string[]) => {
      if (ids.length === 0) throw new Error('No locations selected')
      const site = activeSite
      if (!site || !activeSiteId) throw new Error('No active site')

      // Flatten the tree to an id→node map so we can look up each
      // selected id without walking on every iteration.
      const byId = new Map<string, LocationTreeNode>()
      function walk(nodes: LocationTreeNode[]) {
        for (const n of nodes ?? []) { byId.set(n.id, n); walk(n.children ?? []) }
      }
      walk(tree)

      // Preload the org logo once — every card in the batch reuses
      // the same HTMLImageElement instead of re-downloading per QR.
      const logoBitmap = await loadLogo(orgPrefs.qr?.logo_url ?? null)

      const cards: { canvas: HTMLCanvasElement; title: string }[] = []
      for (const id of ids) {
        const node = byId.get(id)
        if (!node) continue
        const { canvas } = await renderQrCard({
          payload: orgSlug
            ? qrPayload.locationReport(orgSlug, activeSiteId, node.id)
            : qrPayload.location(activeSiteId, node.id),
          title: node.name,
          subtitle: node.type,
          kind: 'Location',
          qr: orgPrefs.qr,
          logoBitmap,
        })
        cards.push({ canvas, title: node.name })
      }
      if (cards.length === 0) throw new Error('None of the selected locations were resolvable')
      await downloadQrPdf(`${safeFilename(site.name)}-qr-codes`, cards)
      return cards.length
    },
    onSuccess: (n) => toast.success(`Downloaded PDF with ${n} QR code${n === 1 ? '' : 's'}`),
    onError: (e) => toast.error(e instanceof Error ? e.message : 'QR export failed'),
  })
  function handleBulkQrZip() {
    if (bulk.ids.length === 0) return
    bulkQrZipM.mutate(bulk.ids)
  }

  const bulkDeleteM = useMutation({
    mutationFn: (ids: string[]) => api.locations.bulkRemove(activeSiteId!, ids),
    onSuccess: (res) => {
      const okCount = res.deleted.length
      const skipCount = res.skipped.length
      const cascaded = res.cascaded ?? 0
      if (okCount > 0 && skipCount === 0 && cascaded === 0) {
        toast.success(`Deleted ${okCount} location${okCount === 1 ? '' : 's'}`)
      } else if (okCount > 0 && cascaded > 0) {
        toast.success(`Deleted ${okCount} (+ ${cascaded} descendant${cascaded === 1 ? '' : 's'})`)
      } else if (okCount > 0 && skipCount > 0) {
        toast.success(`Deleted ${okCount}, skipped ${skipCount}`)
      } else {
        toast.error('Nothing was deleted')
      }
      qc.invalidateQueries({ queryKey: ['location-tree', activeSiteId] })
      bulk.clear()
      if (selectedLocationId && res.deleted.includes(selectedLocationId)) {
        setSelection({ type: 'site' })
      }
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Bulk delete failed'),
  })
  // Count every descendant under the user-picked ids so the confirm
  // copy can show "5 selected · 12 descendants will also be removed".
  function countDescendantsUnder(ids: Set<string>): number {
    let n = 0
    function walk(nodes: LocationTreeNode[], underPicked: boolean) {
      for (const node of nodes ?? []) {
        const isPicked = ids.has(node.id)
        // Children of a picked node are descendants — count once, even
        // if a descendant is also independently in the picked set (the
        // server dedupes via the recursive CTE so the toast and confirm
        // numbers stay aligned).
        if (underPicked && !isPicked) n++
        walk(node.children ?? [], underPicked || isPicked)
      }
    }
    walk(tree, false)
    return n
  }
  async function handleBulkDelete() {
    const ids = bulk.ids
    if (ids.length === 0) return
    const descendants = countDescendantsUnder(new Set(ids))
    const tail = descendants > 0
      ? ` plus ${descendants} descendant${descendants === 1 ? '' : 's'} under them`
      : ''
    const ok = await confirmDlg({
      title: `Delete ${ids.length} location${ids.length === 1 ? '' : 's'}?`,
      message: `They (${tail ? `and ${descendants} child location${descendants === 1 ? '' : 's'}` : 'no child locations'}) will be soft-deleted. You can re-import them anytime.`,
      confirmLabel: 'Delete',
      variant: 'danger',
    })
    if (ok) bulkDeleteM.mutate(ids)
  }

  // Handle right-click actions from tree
  async function handleContextAction(action: TreeContextAction) {
    switch (action.type) {
      case 'edit':
        if (!can('locations:update')) { toast.error("You don't have permission to edit locations"); return }
        setEditTarget(action.node)
        break
      case 'add-child':
        if (!can('locations:create')) { toast.error("You don't have permission to create locations"); return }
        setCreateTarget2(action.node)
        break
      case 'delete': {
        if (!can('locations:delete')) { toast.error("You don't have permission to delete locations"); return }
        const hasChildren = (action.node.children ?? []).length > 0
        if (hasChildren) {
          toast.error('Cannot delete — has sub-locations. Remove them first.')
          return
        }
        const ok = await confirmDlg({
          title: `Delete ${action.node.name}?`,
          message: 'This location will be permanently removed.',
          confirmLabel: 'Delete',
          variant: 'danger',
        })
        if (ok) deleteMutation.mutate(action.node.id)
        break
      }
    }
  }

  const isLoading = sitesQ.isLoading || treeQ.isLoading || (urlLocationId && !resolvedFromUrl && urlLocQ.isLoading)

  // In site mode the toolbar shows the back button + a local search
  // for the location tree (search state is reused for both views; in
  // all-sites mode the FilterStrip owns the input instead).
  const toolbar = viewMode === 'site' ? (
    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
      <Button
        variant="text"
        size="small"
        startIcon={<ArrowBackRoundedIcon sx={{ fontSize: 16 }} />}
        onClick={handleBackToAllSites}
        sx={{ fontSize: 12.5, fontWeight: 600, color: 'primary.main',
          textTransform: 'none', minWidth: 0 }}
      >
        All sites
      </Button>
      <TextField placeholder="Search locations"
        value={search} onChange={(e) => setSearch(e.target.value)} size="small"
        sx={{ width: { xs: '100%', md: 300 }, '& .MuiOutlinedInput-root': { bgcolor: 'background.paper', fontSize: 13, borderRadius: 2, '& fieldset': { borderColor: 'divider' } } }}
      />
    </Stack>
  ) : null

  return (
    <>
      <PageShell title="Locations" toolbar={toolbar} scrollContent={false}
        actions={
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={() => navigate('/settings/sites')}
              sx={{ fontSize: 13, color: 'text.secondary', borderColor: 'divider' }}>Manage sites</Button>
            <Tooltip title={can('locations:create') ? '' : "You don't have permission"} arrow>
              <Box component="span" sx={{ display: 'inline-flex' }}><Button variant="outlined" onClick={() => setImportOpen(true)} disabled={!activeSiteId || !can('locations:create')}
                startIcon={<FileUploadOutlinedIcon sx={{ fontSize: 16 }} />}
                sx={{ fontSize: 13, color: 'text.secondary', borderColor: 'divider' }}>Import</Button></Box>
            </Tooltip>
            <Tooltip title={!can('locations:create') ? "You don't have permission" : createTarget ? `Create a location inside "${createTarget.name}"` : ''} arrow>
              <span>
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }} transition={{ type: 'spring', stiffness: 400, damping: 28 }}>
                  <Button variant="contained" startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                    onClick={() => setCreateRootOpen(true)} disabled={!activeSiteId || !can('locations:create')}
                    sx={{ fontSize: 13, fontWeight: 600, borderRadius: 2, boxShadow: primary.glow }}>
                    New location{createTarget ? ` in ${createTarget.name}` : ''}
                  </Button>
                </motion.div>
              </span>
            </Tooltip>
          </Stack>
        }>
        <Box sx={{
          flex: 1, minHeight: 0,
          display: 'flex', flexDirection: 'column',
          border: '1px solid', borderColor: 'divider', borderRadius: 2,
          overflow: 'hidden', bgcolor: 'background.paper',
          mb: { xs: 3, md: 4 },
        }}>
          {/* Filter strip — only in all-sites mode, only when we have
              data to filter against. */}
          {!isLoading && sites.length > 0 && viewMode === 'all-sites' && (
            <FilterStrip
              allSites={sites}
              filteredCount={filteredSites.length}
              isDark={isDark}
              search={search}
              onSearchChange={setSearch}
              onPickSite={handlePickSite}
              entityFilter={entityFilter}
              onEntityFilterChange={setEntityFilter}
              stateFilter={stateFilter}
              onStateFilterChange={setStateFilter}
              categoryFilter={categoryFilter}
              onCategoryFilterChange={setCategoryFilter}
              colorBy={colorBy}
              onColorByChange={setColorBy}
            />
          )}
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex',
            flexDirection: { xs: 'column', md: 'row' } }}>
          {isLoading ? (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8 }}><CircularProgress size={28} /></Box>
          ) : sites.length === 0 ? (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>No sites yet</Typography>
              <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 2 }}>Create a site first, then add locations.</Typography>
              <Button variant="outlined" onClick={() => navigate('/settings/sites')} startIcon={<SettingsOutlinedIcon sx={{ fontSize: 16 }} />}
                sx={{ fontSize: 13, color: 'primary.main', borderColor: 'divider' }}>Go to Sites</Button>
            </Box>
          ) : viewMode === 'all-sites' ? (
            <>
              <AllSitesSidebar
                sites={filteredSites}
                totalCount={sites.length}
                selectedSiteId={null}
                onSelectSite={handlePickSite}
                colorBy={colorBy}
                getColor={getSiteColor}
              />
              <MapView
                sites={filteredSites}
                selectedSiteId={null}
                onSelectSite={handlePickSite}
                getPinColor={getSiteColor}
                colorBy={colorBy}
                legend={legend}
                highlightedLegendValue={highlightedLegendValue}
                onLegendValueClick={setHighlightedLegendValue}
                getDimensionValue={getSiteDimensionValue}
              />
            </>
          ) : (
            <>
              <LocationTree
                sites={sites}
                selectedSiteId={activeSiteId}
                onSelectSite={(id) => { setSelectedSiteId(id); handleSelectSite() }}
                tree={tree}
                selectedId={selectedLocationId}
                selectedType={selection?.type ?? null}
                onSelect={handleSelectLocation}
                onSelectSiteDetail={handleSelectSite}
                onContextAction={handleContextAction}
                search={search}
                // Bulk selection is exposed for both bulk-delete AND
                // bulk-QR export. QR generation is fully client-side
                // and non-destructive, so anyone who can read
                // locations gets the checkbox column; delete just
                // hides the destructive action if they lack rights.
                bulk={bulk}
                onBulkDelete={can('locations:delete') ? handleBulkDelete : undefined}
                bulkDeleting={bulkDeleteM.isPending}
                onBulkQrZip={handleBulkQrZip}
                bulkQrZipping={bulkQrZipM.isPending}
              />

              {selection?.type === 'site' && activeSite && (
                <SiteDetailPane site={activeSite} locationCount={countAll(tree)} />
              )}
              {selection?.type === 'location' && selectedLocation && activeSiteId && (
                <LocationDetail location={selectedLocation} siteId={activeSiteId} tree={tree}
                  onSelect={handleSelectLocation} />
              )}
            </>
          )}
          </Box>
        </Box>
      </PageShell>

      <CategoriesDialog open={catDialogOpen} onClose={() => setCatDialogOpen(false)} />

      {/* Create from toolbar button */}
      {activeSiteId && (
        <CreateDialog
          open={createRootOpen}
          onClose={() => setCreateRootOpen(false)}
          siteId={activeSiteId}
          parentId={createTarget?.parentId}
          parentType={createTarget?.parentType}
        />
      )}

      {/* Create from right-click "Add sub-location" */}
      {activeSiteId && createTarget2 && (
        <CreateDialog
          open={Boolean(createTarget2)}
          onClose={() => setCreateTarget2(null)}
          siteId={activeSiteId}
          parentId={createTarget2.id}
          parentType={createTarget2.type}
        />
      )}

      {/* Edit from right-click */}
      {activeSiteId && editTarget && (
        <EditDialogFromTree
          open={Boolean(editTarget)}
          onClose={() => setEditTarget(null)}
          siteId={activeSiteId}
          location={editTarget}
        />
      )}

      {/* Import dialog */}
      {activeSiteId && (
        <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} siteId={activeSiteId} />
      )}
    </>
  )
}

function countAll(nodes?: LocationTreeNode[] | null): number {
  if (!Array.isArray(nodes)) return 0
  return nodes.reduce((s, n) => s + 1 + countAll(n.children ?? []), 0)
}

// ─── Edit dialog triggered from right-click ─────────────────────────────────

function EditDialogFromTree({ open, onClose, siteId, location }: {
  open: boolean; onClose: () => void; siteId: string; location: LocationTreeNode
}) {
  const qc = useQueryClient()
  const [name, setName] = useState(location.name)
  const [desc, setDesc] = useState(location.description ?? '')

  const m = useMutation({
    mutationFn: () => api.locations.update(siteId, location.id, { name: name.trim(), description: desc.trim() || null }),
    onSuccess: () => { toast.success('Updated'); qc.invalidateQueries({ queryKey: ['location-tree', siteId] }); onClose() },
    onError: (e: any) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Edit {location.name}</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 2 }}>Type ({location.type}) is immutable.</Typography>
        <Stack spacing={2}>
          <TextField label="Name" required value={name} onChange={(e) => setName(e.target.value)} size="small" fullWidth autoFocus />
          <TextField label="Description" value={desc} onChange={(e) => setDesc(e.target.value)} size="small" fullWidth multiline minRows={2} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ color: 'text.secondary' }}>Cancel</Button>
        <Button variant="contained" onClick={() => m.mutate()} disabled={!name.trim() || m.isPending}>{m.isPending ? 'Saving…' : 'Save'}</Button>
      </DialogActions>
    </Dialog>
  )
}

// ─── Site detail pane ───────────────────────────────────────────────────────

function SiteDetailPane({ site, locationCount }: { site: Site; locationCount: number }) {
  const navigate = useNavigate()
  const { formatDate } = useOrgSettings()
  const { can } = usePermissions()
  const fieldLabels = useSiteFieldLabels()
  const hasCoords = site.latitude != null && site.longitude != null

  // Public-QR sub-state: template picker + QR sticker dialog. Same
  // affordances as Settings → Sites → <site>, exposed here so admins
  // don't have to context-switch out of the Locations module.
  const orgSlug = useAuthStore((s) => s.user?.org_slug ?? null)
  const [qrOpen, setQrOpen] = useState(false)
  const qc = useQueryClient()

  const templatesQ = useQuery({
    queryKey: ['issue-templates'],
    queryFn: () => api.issueTemplates.list(),
  })
  const templates = Array.isArray(templatesQ.data) ? templatesQ.data : []
  const activeTemplate = templates.find((t) => t.id === site.issue_template_id) ?? null

  const assignM = useMutation({
    mutationFn: (templateId: string | null) =>
      api.sites.update(site.id, { issue_template_id: templateId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sites'] })
      qc.invalidateQueries({ queryKey: ['issue-templates'] })
      toast.success('Template assignment saved')
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to save'),
  })

  // Tag assignment on the site. Uses the same PATCH /sites/:id
  // endpoint with `tag_ids` for a one-shot overwrite. Autosaves on
  // every add / remove — no explicit "Save" button.
  const saveTagsM = useMutation({
    mutationFn: (tagIds: string[]) =>
      api.sites.update(site.id, { tag_ids: tagIds }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sites'] }),
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to save tags'),
  })

  return (
    <motion.div key={`site-${site.id}`} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.26 }}
      style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, minHeight: 0 }}>
      <Box sx={{ px: 4, pt: 3.5, pb: 2.5, flexShrink: 0 }}>
        <Stack direction="row" alignItems="flex-start" spacing={2.5}>
          <Box sx={{
            width: 52, height: 52, borderRadius: 2, flexShrink: 0,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: 'primary.main', color: 'primary.contrastText',
          }}>
            <BusinessOutlinedIcon sx={{ fontSize: 26 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.2 }}>{formatSiteName(site)}</Typography>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
              <Chip label="Site" size="small" sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: (t: any) => alpha(t.palette.primary.main, 0.12), color: 'primary.main' }} />
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                {locationCount} location{locationCount === 1 ? '' : 's'}
              </Typography>
            </Stack>
            {site.address && (
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 1 }}>
                <LocationOnOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{site.address}</Typography>
              </Stack>
            )}
            {site.description && (
              <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.75, lineHeight: 1.55 }}>{site.description}</Typography>
            )}
          </Box>
          <Button variant="outlined" size="small" startIcon={<EditOutlinedIcon sx={{ fontSize: 14 }} />}
            onClick={() => navigate(`/settings/sites/${site.id}?edit=1`)}
            sx={{ fontSize: 12.5, fontWeight: 600, color: 'primary.main', borderColor: 'divider', flexShrink: 0 }}>
            Edit in Settings
          </Button>
        </Stack>
      </Box>
      <Divider />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 4, py: 3,
        scrollbarWidth: 'thin', scrollbarColor: 'transparent transparent',
        '&::-webkit-scrollbar': { width: 8 }, '&::-webkit-scrollbar-thumb': { background: 'transparent', borderRadius: 99 },
        '&:hover::-webkit-scrollbar-thumb': { background: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.12)' } }}>

        {hasCoords && (
          <Box sx={{ mb: 3 }}>
            <MapPicker latitude={site.latitude} longitude={site.longitude} height={240} onChange={() => {}} />
          </Box>
        )}

        {/* Quick-stat tiles for the most-scanned fields ----------- */}
        <KvTiles items={[
          site.entity   ? { label: 'Entity',   value: site.entity }   : null,
          site.state    ? { label: 'State',    value: site.state }    : null,
          site.category ? { label: 'Category', value: site.category } : null,
          site.branch_area_sqft != null
            ? { label: 'Branch area', value: `${site.branch_area_sqft.toLocaleString()} sq ft` }
            : null,
        ].filter(Boolean) as { label: string; value: string }[]} />

        {/* Identity ---------------------------------------------- */}
        <DetailSection title="Identity">
          <Kv label="Name" value={site.name} />
          {site.description && <Kv label="Description" value={site.description} />}
          {site.address && <Kv label="Address" value={site.address} />}
          {hasCoords && (
            <Kv label="Coordinates" value={`${site.latitude!.toFixed(6)}, ${site.longitude!.toFixed(6)}`} />
          )}
        </DetailSection>

        {/* Tags -------------------------------------------------- */}
        <DetailSection title="Tags">
          <Box sx={{ px: 0.5, py: 0.5 }}>
            <TagsField
              value={site.tags ?? []}
              onChange={(next) => saveTagsM.mutate(next.map((t) => t.id))}
              helperText="Group sites by tag on the detailed overview."
            />
          </Box>
        </DetailSection>

        {/* Public QR — Report an issue --------------------------- */}
        <DetailSection title="Public QR — Report an issue">
          <Stack spacing={2} sx={{ px: 0.5, py: 0.5 }}>
            <Box>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary', mb: 0.75 }}>
                Assigned issue template
              </Typography>
              <Select fullWidth size="small"
                value={site.issue_template_id ?? ''}
                disabled={!can('sites:update') || assignM.isPending}
                onChange={(e) => assignM.mutate(e.target.value === '' ? null : (e.target.value as string))}
                displayEmpty
              >
                <MenuItem value="">
                  <em style={{ color: '#9CA3AF' }}>
                    No template — reporters see a free-text input
                  </em>
                </MenuItem>
                {templates.filter((t) => t.is_active).map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <QuestionAnswerOutlinedIcon sx={{ fontSize: 16, color: '#3B82F6' }} />
                      <span>{t.name}</span>
                      <Chip size="small" label={`${t.active_item_count} items`} sx={{
                        height: 18, fontSize: 10, fontWeight: 700,
                        bgcolor: '#3B82F622', color: '#3B82F6',
                      }} />
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
              {activeTemplate && (
                <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.75 }}>
                  Reporters at this site see the {activeTemplate.active_item_count} active item{activeTemplate.active_item_count === 1 ? '' : 's'} from <strong>{activeTemplate.name}</strong>. Configure them from{' '}
                  <Box component="span" onClick={() => navigate('/settings?tab=issue-templates')}
                    sx={{ color: 'primary.main', textDecoration: 'underline', cursor: 'pointer' }}>
                    Settings → Issue Templates
                  </Box>.
                </Typography>
              )}
            </Box>
            <Box>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary', mb: 0.75 }}>
                Site QR sticker
              </Typography>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Button variant="outlined" size="small" startIcon={<QrCode2OutlinedIcon sx={{ fontSize: 16 }} />}
                  onClick={() => setQrOpen(true)}
                  disabled={!orgSlug}
                  sx={{ borderRadius: 2, fontWeight: 600 }}>
                  Print QR
                </Button>
                <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>
                  Prints an SOS sticker linked to this site. Scanners without the mobile app see the "Report an issue" form.
                </Typography>
              </Stack>
            </Box>
          </Stack>
        </DetailSection>

        {/* Site-level SOS QR sticker (public "Report an issue" URL). */}
        {orgSlug && (
          <QrCodeDialog open={qrOpen} onClose={() => setQrOpen(false)}
            payload={qrPayload.siteReport(orgSlug, site.id)}
            title={formatSiteName(site)}
            kind="Site" />
        )}

        {/* Classification — entity / state / category / area ----- */}
        {(site.entity || site.state || site.category || site.branch_area_sqft != null) && (
          <DetailSection title="Classification">
            {site.entity   && <Kv label="Entity"   value={site.entity} />}
            {site.state    && <Kv label="State"    value={site.state} />}
            {site.category && <Kv label="Category" value={site.category} />}
            {site.branch_area_sqft != null && (
              <Kv label="Branch area" value={`${site.branch_area_sqft.toLocaleString()} sq ft`} />
            )}
          </DetailSection>
        )}

        {/* Customer-defined fields (only render those that are set) */}
        {(site.field1 || site.field2 || site.field3 || site.field4 || site.field5) && (
          <DetailSection title="Custom fields">
            {site.field1 && <Kv label={fieldLabels.field1} value={site.field1} />}
            {site.field2 && <Kv label={fieldLabels.field2} value={site.field2} />}
            {site.field3 && <Kv label={fieldLabels.field3} value={site.field3} />}
            {site.field4 && <Kv label={fieldLabels.field4} value={site.field4} />}
            {site.field5 && <Kv label={fieldLabels.field5} value={site.field5} />}
          </DetailSection>
        )}

        {/* Footer — locations + creation date -------------------- */}
        <DetailSection title="Activity">
          <Kv label="Locations" value={`${locationCount} location${locationCount === 1 ? '' : 's'}`} />
          <Kv label="Created" value={formatDate(site.created_at)} />
        </DetailSection>
      </Box>
    </motion.div>
  )
}

// Small section wrapper used by SiteDetailPane.
function DetailSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ py: 2 }}>
      <Typography sx={{
        fontSize: 10.5, fontWeight: 800, letterSpacing: 0.7,
        textTransform: 'uppercase', color: 'text.disabled', mb: 1.25,
      }}>
        {title}
      </Typography>
      <Stack spacing={1.25}>{children}</Stack>
    </Box>
  )
}

// Compact tile-grid for the most-scanned classification fields. Renders
// 0–4 tiles depending on which fields are populated; hides itself if
// the input array is empty (so unconfigured sites don't show a blank
// row).
function KvTiles({ items }: { items: { label: string; value: string }[] }) {
  if (items.length === 0) return null
  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: `repeat(${Math.min(items.length, 4)}, 1fr)` },
      gap: 1, mt: 1, mb: 1.5,
    }}>
      {items.map((it) => (
        <Box key={it.label} sx={{
          p: 1.25, borderRadius: 2,
          bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
          border: '1px solid', borderColor: (t) => alpha(t.palette.primary.main, 0.12),
        }}>
          <Typography sx={{
            fontSize: 10, fontWeight: 800, letterSpacing: 0.6,
            textTransform: 'uppercase', color: 'text.disabled', mb: 0.25,
          }}>
            {it.label}
          </Typography>
          <Typography sx={{
            fontSize: 13, fontWeight: 700, lineHeight: 1.3,
            overflow: 'hidden', textOverflow: 'ellipsis',
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          }}>
            {it.value}
          </Typography>
        </Box>
      ))}
    </Box>
  )
}

function Kv({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" alignItems="flex-start" spacing={1.5}>
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', minWidth: 100 }}>{label}</Typography>
      <Typography sx={{ fontSize: 13, fontWeight: 500, flex: 1, wordBreak: 'break-word' }}>{value}</Typography>
    </Stack>
  )
}
